import { useEffect, useMemo, useState } from 'react';
import { Pressable, Text, View } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import SheetModal from '@/components/General/SheetModal';
import SortableList from '@/components/General/SortableList';
import Checkbox from '@/components/General/inputs/Checkbox';
import Unicon from '@/components/General/Unicon';

const PeopleColumnSettingsModal = ({ isOpen, onClose, columns, selectedColumnIds, defaultColumnIds, onSave, includeCards = true }) => {
    const [draftIds, setDraftIds] = useState(selectedColumnIds);
    const [isReordering, setIsReordering] = useState(false);

    useEffect(() => {
        if (isOpen) setDraftIds(selectedColumnIds);
    }, [isOpen, selectedColumnIds]);

    const columnMap = useMemo(() => new Map(columns.map(column => [column.id, column])), [columns]);
    const selectedColumns = draftIds.map(id => columnMap.get(id)).filter(Boolean);
    const availableColumns = columns.filter(column => !draftIds.includes(column.id));
    const sortableColumns = selectedColumns.filter(column => column.id !== 'name');

    const toggleColumn = (columnId) => {
        if (columnId === 'name') return;
        setDraftIds(ids => ids.includes(columnId) ? ids.filter(id => id !== columnId) : [...ids, columnId]);
    };

    const applyReorder = (reordered) => {
        setDraftIds(['name', ...reordered.map(column => column.id)]);
    };

    const row = (column, selected) => (
        <View
            key={column.id}
            style={styles.columnRow}
        >
            <Checkbox testID={`PeopleColumn-${column.id}`} checked={selected} disabled={column.id === 'name'} onChange={() => toggleColumn(column.id)} />
            <View style={styles.columnText}>
                <Text style={styles.columnName}>{column.value}</Text>
                {column.section && <Text style={styles.columnSection}>{column.section}</Text>}
            </View>
            {column.id === 'name' && <Text style={styles.requiredText}>Always shown</Text>}
        </View>
    );

    return (
        <SheetModal
            isOpen={isOpen}
            onCancel={onClose}
            onConfirm={() => {
                onSave(draftIds);
                onClose();
            }}
            title="Directory Columns"
            subtitle={includeCards ? 'Choose what appears in table and card views' : 'Choose what appears in the directory table'}
            cancelText="Cancel"
            confirmText="Save"
            width={560}
        >
            <View style={styles.content}>
                <View style={styles.sectionHeader}>
                    <View>
                        <Text style={styles.sectionTitle}>Visible columns</Text>
                        <Text style={styles.helpText}>{includeCards ? 'First two fields appear on collapsed cards' : 'Drag selected columns into the order you need'}</Text>
                    </View>
                    {sortableColumns.length > 1 && (
                        <Pressable testID="PeopleColumnsReorder" onPress={() => setIsReordering(true)} style={styles.reorderButton}>
                            <Unicon name="reorder-four-outline" size={18} colorVariant="primary" />
                            <Text style={styles.reorderText}>Reorder</Text>
                        </Pressable>
                    )}
                </View>
                <View style={styles.list}>{selectedColumns.map(column => row(column, true))}</View>

                {availableColumns.length > 0 && (
                    <View style={styles.section}>
                        <Text style={styles.sectionTitle}>Available columns</Text>
                        <View style={styles.list}>{availableColumns.map(column => row(column, false))}</View>
                    </View>
                )}

                <Pressable testID="PeopleColumnsReset" onPress={() => setDraftIds(defaultColumnIds)} style={styles.resetButton}>
                    <Unicon name="refresh-outline" size={18} colorVariant="error" />
                    <Text style={styles.resetText}>Reset to defaults</Text>
                </Pressable>
            </View>

            <SortableList
                items={sortableColumns}
                itemContent={(column) => <Text style={styles.sortableText}>{column.value}</Text>}
                isOpen={isReordering}
                close={() => setIsReordering(false)}
                onConfirm={applyReorder}
                title="Reorder Columns"
                helpText="Drag fields into the order they should appear"
            />
        </SheetModal>
    );
};

const styles = StyleSheet.create((theme) => ({
    content: {
        gap: theme.spacing.md,
        paddingBottom: theme.spacing.xl,
    },
    section: {
        gap: theme.spacing.sm,
    },
    sectionHeader: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing.sm,
    },
    sectionTitle: {
        color: theme.colors.text,
        fontFamily: theme.fonts.bold,
        fontSize: theme.typography.body,
    },
    helpText: {
        color: theme.colors.textSecondary,
        fontFamily: theme.fonts.body,
        fontSize: theme.typography.caption,
        marginTop: 2,
    },
    list: {
        gap: theme.spacing.sm,
    },
    columnRow: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: theme.spacing.sm,
        padding: 12,
        backgroundColor: theme.colors.surface,
        borderRadius: theme.roundness.md,
        boxShadow: theme.shadows.card,
        borderCurve: 'continuous',
    },
    columnText: {
        flex: 1,
    },
    columnName: {
        color: theme.colors.text,
        fontFamily: theme.fonts.body,
        fontSize: theme.typography.body,
    },
    columnSection: {
        color: theme.colors.textTertiary,
        fontFamily: theme.fonts.body,
        fontSize: theme.typography.micro,
        marginTop: 2,
    },
    requiredText: {
        color: theme.colors.textTertiary,
        fontFamily: theme.fonts.body,
        fontSize: theme.typography.micro,
    },
    reorderButton: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: 4,
        padding: 8,
    },
    reorderText: {
        color: theme.colors.primary,
        fontFamily: theme.fonts.bold,
        fontSize: theme.typography.caption,
    },
    resetButton: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing.sm,
        padding: 12,
    },
    resetText: {
        color: theme.colors.error,
        fontFamily: theme.fonts.bold,
        fontSize: theme.typography.caption,
    },
    sortableText: {
        color: theme.colors.text,
        fontFamily: theme.fonts.body,
        fontSize: theme.typography.body,
    },
}));

export default PeopleColumnSettingsModal;
